import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SaveIcon from '@material-ui/icons/Save'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import axios from 'axios'

const STATUS_COLORS = {
  draft: '#90a4ae',
  sent: '#42a5f5',
  accepted: '#66bb6a',
  declined: '#ef5350'
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: '#fff',
    color: '#333',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 56
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  title: {
    fontWeight: 600,
    fontSize: 16
  },
  statusChip: {
    textTransform: 'capitalize',
    fontWeight: 600,
    fontSize: 11
  },
  saveBtn: {
    textTransform: 'none'
  },
  pdfBtn: {
    textTransform: 'none'
  },
  savingText: {
    fontSize: 12,
    color: '#999',
    marginRight: 8
  },
  templateSelect: {
    minWidth: 160,
    marginLeft: 16
  }
}))

const ProposalHeader = ({
  proposal,
  saving,
  dirty,
  onSave,
  onBack,
  url,
  templates,
  onTemplateChange
}) => {
  const classes = useStyles()
  const [exporting, setExporting] = useState(false)

  const handleExportPdf = async () => {
    if (!proposal?._id || !url) return
    setExporting(true)
    try {
      const res = await axios.post(`${url}/api/mtc-proposals/${proposal._id}/pdf`, {}, {
        responseType: 'blob'
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${proposal.crmSnapshot?.clientName || 'Proposal'}_MTC_Proposal.pdf`
      link.click()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.error('PDF export failed:', err)
      alert('PDF export is not yet configured. The preview can be printed from the browser.')
    } finally {
      setExporting(false)
    }
  }

  const clientName = proposal?.crmSnapshot?.clientName || 'New Proposal'
  const status = proposal?.status || 'draft'

  return (
    <AppBar position="static" className={classes.appBar} elevation={0}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.left}>
          {onBack && (
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography className={classes.title}>
            {clientName}
          </Typography>
          <Chip
            label={status}
            size="small"
            className={classes.statusChip}
            style={{ backgroundColor: STATUS_COLORS[status] || '#ccc', color: '#fff' }}
          />
          {templates && templates.length > 0 && (
            <FormControl size="small" className={classes.templateSelect}>
              <InputLabel>Template</InputLabel>
              <Select
                value={proposal?.templateId?._id || proposal?.templateId || ''}
                onChange={(e) => onTemplateChange && onTemplateChange(e.target.value)}
                label="Template"
              >
                {templates.map(t => (
                  <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <div className={classes.right}>
          {saving && <span className={classes.savingText}>Saving...</span>}
          {dirty && !saving && <span className={classes.savingText}>Unsaved changes</span>}
          <Button
            variant="outlined"
            size="small"
            startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            onClick={onSave}
            disabled={saving}
            className={classes.saveBtn}
          >
            Save
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={exporting}
            className={classes.pdfBtn}
          >
            Export PDF
          </Button>
        </div>
      </Toolbar>
    </AppBar>
  )
}

export default ProposalHeader
